<template>
  <div
    v-if="placeholderPosition !== null && dropDuration && dropDuration >= 5"
    class="dragging-placeholder"
    :class="{ duplicate: isDuplicatingAllocation }"
    :style="{
      top: `${placeholderPosition}px`,
    }"
  >
    <span v-if="dropTime" class="drop-timer">{{
      locale.formatDaytime(getDaytime(dropTime))
    }}</span>
    <span v-if="isDuplicatingAllocation" class="duplicate-plus">+</span>
    <div class="drop-line" />
    <div v-if="showCalendarInfo && calendar" class="calendar-info">
      <RTag
        :theme="`calendar-${calendar.color.toLowerCase()}`"
        class="calendar-indicator"
      />
      <span class="calendar-name">{{ calendar.summary }}</span>
    </div>
    <span class="drop-duration">{{
      dropDuration > 15 ? "30 mins" : `${dropDuration} mins`
    }}</span>
    <div class="drop-line drop-line-end" />
  </div>
</template>
<script lang="ts">
import { defineComponent, PropType } from "vue";

import { Calendar } from "@ctrl/controller-rpc";

import { getDaytime } from "@/components/dates";
import RTag from "@/components/elements/RTag.vue";
import * as inject from "@/components/inject";

export default defineComponent({
  components: {
    RTag,
  },
  props: {
    dropDuration: {
      type: Number as PropType<number>,
      required: false,
    },
    dropTime: {
      type: Object as PropType<Date>,
      required: false,
    },
    placeholderPosition: {
      type: Number as PropType<number>,
      required: false,
    },
    calendar: {
      type: Object as PropType<Calendar>,
      required: false,
    },
    showCalendarInfo: {
      type: Boolean,
      required: false,
      default: false,
    },
    isDuplicatingAllocation: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup() {
    const locale = inject.locale();
    return { getDaytime, locale };
  },
});
</script>
<style lang="scss" scoped>
.dragging-placeholder {
  position: absolute;
  inset-inline-start: 4px;
  inset-inline-end: 0;
  display: flex;
  z-index: $z-index-content-elevated;
  align-items: center;
  gap: 4px;

  .drop-timer {
    padding: 2px 6px;
    border-radius: 3px;
    @extend %font-caption-regular;
    @include themed() {
      background-color: t($secondary);
      color: t($bg);
    }
  }

  .calendar-info {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;

    .calendar-indicator {
      flex-shrink: 0;
    }

    .calendar-name {
      @extend %font-caption-regular;
      font-size: 11px;
      white-space: nowrap;
      @include themed() {
        color: t($grey-60);
      }
    }
  }

  .drop-duration {
    @extend %font-caption-regular;
    padding: 2px 6px;
    white-space: nowrap;
    @include themed() {
      color: t($secondary);
    }
  }

  .drop-line.drop-line-end {
    width: 15px;
  }

  .drop-line {
    margin: auto 0;
    height: 2px;
    width: 100%;

    @include themed() {
      background-color: t($secondary);
    }
  }

  .duplicate-badge {
    @extend %font-caption-regular;
    padding: 2px 6px;
    border-radius: 3px;
    border: 1px dashed;
    white-space: nowrap;
    position: relative;
    z-index: $z-index-content-elevated;
    @include themed() {
      border-color: t($secondary);
      color: t($secondary);
      background-color: t($bg);
    }
  }

  .duplicate-plus {
    margin-inline-start: 6px;
    margin-inline-end: 6px;
    @extend %font-caption-regular;
    line-height: 1;
    opacity: 0.7;
    @include themed() {
      color: t($secondary);
    }
  }
}
</style>
